import React from "react";
import { motion } from "framer-motion";
import { FileBadge, CalendarRange, Phone, Settings2 } from "lucide-react";

const criteria = [
  {
    icon: FileBadge,
    title: "1 tax ID",
    detail:
      "A single legal billing entity for claims, payouts, EFT/ERA enrollment, and merchant processing.",
  },
  {
    icon: CalendarRange,
    title: "1 scheduling config",
    detail:
      "One set of operatories, providers, hours, and templates managed under a single front-desk workflow.",
  },
  {
    icon: Phone,
    title: "1 set of hardware",
    detail:
      "One physical office footprint — 1 payment terminal and up to 3 physical phones included per location.",
  },
  {
    icon: Settings2,
    title: "1 set of practice settings",
    detail:
      "One configuration of practice rules and clinical protocols that all AI agents follow when acting on behalf of the location.",
  },
];

const SpaCreekLocationCriteriaSlide = () => {
  return (
    <div className="h-full w-full bg-[#f8fafc] flex items-center justify-center p-6 xl:p-10 relative overflow-hidden font-sans">
      {/* Ambient gradient blurs */}
      <div
        className="absolute top-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #93c5fd 0%, #c4b5fd 50%, #fbcfe8 100%)" }}
      />
      <div
        className="absolute bottom-[-200px] left-[-100px] w-[600px] h-[600px] rounded-full blur-[120px] opacity-20 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #c4b5fd 100%)" }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <div className="w-full max-w-[1100px] z-10 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-2">
            Per-location pricing
          </p>
          <h2 className="text-3xl lg:text-[40px] font-bold text-slate-900 tracking-tight leading-tight">
            What defines{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              a "location"?
            </span>
          </h2>
          <p className="text-[14px] text-slate-600 mt-2 max-w-[760px]">
            Our per-location pricing is determined by four operational
            criteria. If any of them is violated — separate tax ID, separate
            scheduling config, separate hardware footprint, or separate
            practice settings — it counts as another location.
          </p>
        </motion.div>

        {/* Criteria grid */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-2 gap-4"
        >
          {criteria.map((c, i) => {
            const Icon = c.icon;
            return (
              <div
                key={c.title}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_12px_36px_-22px_rgba(15,23,42,0.22)] flex gap-4"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-md shadow-blue-500/20">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h3 className="text-[17px] font-semibold text-slate-900 leading-snug">
                      {c.title}
                    </h3>
                  </div>
                  <p className="text-[13px] text-slate-600 leading-relaxed mt-1.5">
                    {c.detail}
                  </p>
                </div>
              </div>
            );
          })}
        </motion.div>

        {/* Rule callout */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.25 }}
          className="mt-5 rounded-xl border border-blue-200 bg-blue-50/60 px-5 py-3.5 flex items-start gap-3"
        >
          <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-blue-700 mt-0.5 shrink-0">
            Rule
          </div>
          <p className="text-[13px] text-slate-700 leading-relaxed">
            All four criteria must hold for a footprint to be priced as{" "}
            <span className="font-semibold text-slate-900">one location</span>.
            The moment any one is duplicated — a second tax ID, a second
            schedule, a second hardware set, or a second set of practice
            settings — it counts as an additional location.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default SpaCreekLocationCriteriaSlide;
